import React, { ReactElement } from "react";

interface IconProps {
  /**
   * Custom class name to be added to the Icon
   */
  className?: string;
  /**
   *  Height and width of the icon
   */
  size?: number;
  /**
   *  Color of the icon
   */
  color?: string;
  /**
   * Name of the icon to use
   */
  name:
    | "home"
    | "logout"
    | "sitemap"
    | "about"
    | "settings"
    | "save"
    | "delete"
    | "edit"
    | "back"
    | "user"
    | "password"
    | "profile"
    | "comment"
    | "notification"
    | "close";
  /**
   *  Function to call on click
   */
  onClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}

const Icon: React.FC<IconProps> = ({
  className = "",
  size,
  color,
  name,
  onClick,
  ...rest
}): ReactElement => {
  const svgList = {
    home: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M12.5179 30.0787V20.4719H19.4744V30.0787C19.4744 31.1354 20.257 32 21.2135 32H26.4308C27.3873 32 28.1699 31.1354 28.1699 30.0787V16.6292H31.1264C31.9264 16.6292 32.309 15.5341 31.7004 14.9577L17.1613 0.489943C16.5005 -0.163314 15.4918 -0.163314 14.8309 0.489943L0.291914 14.9577C-0.299385 15.5341 0.0658289 16.6292 0.865822 16.6292H3.82232V30.0787C3.82232 31.1354 4.60492 32 5.56144 32H10.7788C11.7353 32 12.5179 31.1354 12.5179 30.0787Z"
          fill={color}
        />
      </svg>
    ),
    logout: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill={color}
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M12.6044 22.3822L15.1111 24.8889L24 16L15.1111 7.11111L12.6044 9.61778L17.1911 14.2222H0V17.7778H17.1911L12.6044 22.3822ZM28.4444 0H3.55556C2.61256 0 1.70819 0.374602 1.0414 1.0414C0.374602 1.70819 0 2.61256 0 3.55556V10.6667H3.55556V3.55556H28.4444V28.4444H3.55556V21.3333H0V28.4444C0 29.3874 0.374602 30.2918 1.0414 30.9586C1.70819 31.6254 2.61256 32 3.55556 32H28.4444C30.4 32 32 30.4 32 28.4444V3.55556C32 1.6 30.4 0 28.4444 0Z"
          fill={color}
        />
      </svg>
    ),
    sitemap: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M16 0C7.1776 0 0 7.1776 0 16C0 24.8224 7.1776 32 16 32C24.8224 32 32 24.8224 32 16C32 7.1776 24.8224 0 16 0ZM16 28.8C8.9424 28.8 3.2 23.0576 3.2 16C3.2 8.9424 8.9424 3.2 16 3.2C23.0576 3.2 28.8 8.9424 28.8 16C28.8 23.0576 23.0576 28.8 16 28.8Z"
          fill={color}
        />
        <path
          d="M13.5455 7V11.8H15.1818V14.2H10.2727C9.36455 14.2 8.63636 14.912 8.63636 15.8V18.2H7V23H11.9091V18.2H10.2727V15.8H15.1818V18.2H13.5455V23H18.4545V18.2H16.8182V15.8H21.7273V18.2H20.0909V23H25V18.2H23.3636V15.8C23.3636 14.912 22.6355 14.2 21.7273 14.2H16.8182V11.8H18.4545V7H13.5455Z"
          fill={color}
        />
      </svg>
    ),
    about: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M16 0C7.1776 0 0 7.1776 0 16C0 24.8224 7.1776 32 16 32C24.8224 32 32 24.8224 32 16C32 7.1776 24.8224 0 16 0ZM16 28.8C8.9424 28.8 3.2 23.0576 3.2 16C3.2 8.9424 8.9424 3.2 16 3.2C23.0576 3.2 28.8 8.9424 28.8 16C28.8 23.0576 23.0576 28.8 16 28.8Z"
          fill={color}
        />
        <path d="M14 14.4H18V24H14V14.4ZM14 8H18V11.2H14V8Z" fill={color} />
      </svg>
    ),
    settings: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M21.3201 25.0667C23.4377 25.0667 25.1631 23.3905 25.1631 21.3333C25.1631 19.2762 23.4377 17.6 21.3201 17.6C19.1867 17.6 17.477 19.2762 17.477 21.3333C17.477 23.3905 19.1867 25.0667 21.3201 25.0667ZM29.4768 22.3695L31.7984 24.1371C32.0023 24.3048 32.065 24.5791 31.9239 24.8229L29.7278 28.5105C29.6619 28.6205 29.5578 28.704 29.4341 28.7459C29.3104 28.7878 29.1753 28.7854 29.0533 28.739L26.3239 27.6724C25.7592 28.099 25.1318 28.4495 24.473 28.7238L24.0494 31.5429C24.0181 31.8019 23.7828 32 23.5161 32H19.124C18.8416 32 18.622 31.8019 18.575 31.5429L18.1671 28.7238C17.4926 28.4495 16.8809 28.099 16.3162 27.6724L13.5711 28.739C13.3358 28.8305 13.0378 28.739 12.9123 28.5105L10.7162 24.8229C10.6494 24.7128 10.6253 24.5832 10.6484 24.4574C10.6714 24.3317 10.74 24.2181 10.8417 24.1371L13.1633 22.3695L13.0848 21.3333L13.1633 20.2819L10.8417 18.5295C10.7405 18.4482 10.6723 18.3347 10.6493 18.2091C10.6263 18.0835 10.6501 17.954 10.7162 17.8438L12.9123 14.1562C13.0378 13.9124 13.3358 13.821 13.5711 13.9124L16.3162 14.9943C16.8809 14.5676 17.4926 14.2019 18.1671 13.9429L18.575 11.1086C18.622 10.8648 18.8416 10.6667 19.124 10.6667H23.5161C23.7828 10.6667 24.0181 10.8648 24.0494 11.1086L24.473 13.9429C25.1318 14.2019 25.7592 14.5676 26.3239 14.9943L29.0533 13.9124C29.32 13.821 29.5866 13.9124 29.7278 14.1562L31.9239 17.8438C32.065 18.0724 32.0023 18.3619 31.7984 18.5295L29.4768 20.2819L29.5553 21.3333L29.4768 22.3695ZM6.87314 9.24952C8.23784 9.24952 9.33586 8.18286 9.33586 6.85714C9.33586 5.53143 8.23784 4.44952 6.87314 4.44952C6.21583 4.44952 5.58544 4.70318 5.12065 5.1547C4.65586 5.60622 4.39474 6.2186 4.39474 6.85714C4.39474 8.18286 5.50845 9.24952 6.87314 9.24952ZM12.1123 7.52762L13.6339 8.65524C13.7437 8.7619 13.775 8.94476 13.6809 9.09714L12.2692 11.4743C12.1907 11.6267 12.0025 11.6876 11.8456 11.6267L10.0888 10.941L8.92803 11.6114L8.62999 13.4248C8.59862 13.5924 8.45744 13.7143 8.28489 13.7143H5.46139C5.27316 13.7143 5.13199 13.5924 5.10061 13.4248L4.84964 11.6114L3.65749 10.941L1.86928 11.6267C1.7281 11.6876 1.55555 11.6267 1.46144 11.4743L0.0496868 9.09714C-0.0444299 8.94476 0.00262874 8.7619 0.128118 8.65524L1.6183 7.52762L1.57124 6.85714L1.6183 6.18667L0.128118 5.05905C0.0636805 5.00608 0.0205027 4.93276 0.00616078 4.85195C-0.00818109 4.77115 0.00722888 4.68801 0.0496868 4.61714L1.46144 2.24C1.55555 2.08762 1.7281 2.02667 1.86928 2.08762L3.64181 2.77333L4.84964 2.10286L5.10061 0.289524C5.13199 0.121905 5.27316 0 5.46139 0H8.28489C8.45744 0 8.59862 0.121905 8.62999 0.289524L8.92803 2.10286L10.0888 2.77333L11.8456 2.08762C12.0025 2.02667 12.1907 2.08762 12.2692 2.24L13.6809 4.61714C13.775 4.76952 13.7437 4.95238 13.6339 5.05905L12.1123 6.18667L12.1594 6.85714L12.1123 7.52762Z"
          fill={color}
        />
      </svg>
    ),
    save: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M24.8886 0H2.28571C1.67951 0 1.09812 0.240816 0.66947 0.66947C0.240816 1.09812 0 1.67951 0 2.28571V29.7143C0 30.3205 0.240816 30.9019 0.66947 31.3305C1.09812 31.7592 1.67951 32 2.28571 32H29.7143C30.32 31.9983 30.9004 31.7569 31.3287 31.3287C31.7569 30.9004 31.9983 30.32 32 29.7143V7.11143L24.8886 0ZM5.71429 10.2857V5.71429H19.4286V10.2857H5.71429ZM21.7079 23.1257C21.6558 24.2318 21.2836 25.299 20.6363 26.1975C19.989 27.096 19.0946 27.787 18.0619 28.1866C17.0291 28.5862 15.9025 28.6771 14.8191 28.4482C13.7356 28.2194 12.742 27.6807 11.959 26.8977C11.1761 26.1146 10.6375 25.1209 10.4088 24.0374C10.1801 22.954 10.2711 21.8274 10.6708 20.7947C11.0705 19.762 11.7617 18.8677 12.6602 18.2205C13.5588 17.5733 14.626 17.2012 15.7321 17.1493C16.5263 17.1143 17.319 17.245 18.0598 17.533C18.8007 17.821 19.4736 18.26 20.0356 18.8221C20.5976 19.3842 21.0366 20.0571 21.3245 20.798C21.6123 21.5389 21.7429 22.3316 21.7079 23.1257Z"
          fill={color}
        />
      </svg>
    ),
    delete: (
      <svg
        width="19"
        height="23"
        viewBox="0 0 19 23"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M19 1.27778H14.25L12.8929 0H6.10714L4.75 1.27778H0V3.83333H19V1.27778ZM1.35714 20.4444C1.35714 21.1222 1.64311 21.7722 2.15214 22.2515C2.66117 22.7308 3.35156 23 4.07143 23H14.9286C15.6484 23 16.3388 22.7308 16.8479 22.2515C17.3569 21.7722 17.6429 21.1222 17.6429 20.4444V5.11111H1.35714V20.4444Z"
          fill={color}
        />
      </svg>
    ),
    edit: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path d="M27.0479 0L32 4.95118L28.2248 8.72727L23.2727 3.7761L27.0479 0Z" fill={color} />
        <path
          d="M8.72728 23.2727H13.6479L25.6 11.3206L20.6794 6.4L8.72728 18.3521V23.2727Z"
          fill={color}
        />
        <path
          d="M27.4101 28.6384H8.83633C8.79179 28.6384 8.74554 28.6552 8.70099 28.6552C8.64446 28.6552 8.58793 28.6401 8.52968 28.6384H3.42626V5.10707H15.1561L18.5823 1.74545H3.42626C1.53668 1.74545 0 3.25146 0 5.10707V28.6384C0 30.494 1.53668 32 3.42626 32H27.4101C28.3188 32 29.1903 31.6458 29.8328 31.0154C30.4754 30.385 30.8364 29.5299 30.8364 28.6384V14.0691L27.4101 17.4308V28.6384Z"
          fill={color}
        />
      </svg>
    ),
    back: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M16.439 7.99997C16.439 8.19888 16.3696 8.38965 16.2461 8.5303C16.1226 8.67095 15.9551 8.74997 15.7804 8.74997H2.88133L7.68865 14.2156C7.81231 14.3578 7.88168 14.5498 7.88168 14.75C7.88168 14.9501 7.81231 15.1422 7.68865 15.2843C7.56277 15.423 7.39453 15.5004 7.21944 15.5004C7.04435 15.5004 6.87611 15.423 6.75023 15.2843L0.823405 8.53435C0.699744 8.39215 0.630371 8.20011 0.630371 7.99997C0.630371 7.79984 0.699744 7.60779 0.823405 7.4656L6.75023 0.715596C6.87818 0.595999 7.04062 0.53488 7.20595 0.544128C7.37127 0.553375 7.52767 0.632328 7.64472 0.765629C7.76176 0.89893 7.83109 1.07705 7.83921 1.26534C7.84733 1.45363 7.79366 1.63862 7.68865 1.78435L2.88133 7.24997H15.7804C15.9551 7.24997 16.1226 7.32899 16.2461 7.46964C16.3696 7.61029 16.439 7.80106 16.439 7.99997Z"
          fill={color}
        />
      </svg>
    ),
    user: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M8 7.57895C8 11.7575 11.5893 15.1579 16 15.1579C20.4107 15.1579 24 11.7575 24 7.57895C24 3.40042 20.4107 0 16 0C11.5893 0 8 3.40042 8 7.57895ZM30.2222 32H32V30.3158C32 23.8164 26.416 18.5263 19.5556 18.5263H12.4444C5.58222 18.5263 0 23.8164 0 30.3158V32H30.2222Z"
          fill={color}
        />
      </svg>
    ),
    password: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M27.4286 16C28.641 16 29.8038 16.3371 30.6611 16.9373C31.5184 17.5374 32 18.3513 32 19.2V28.8C32 29.6487 31.5184 30.4626 30.6611 31.0627C29.8038 31.6629 28.641 32 27.4286 32H4.57143C3.35901 32 2.19625 31.6629 1.33894 31.0627C0.481631 30.4626 0 29.6487 0 28.8V19.2C0 18.3513 0.481631 17.5374 1.33894 16.9373C2.19625 16.3371 3.35901 16 4.57143 16V8C4.57143 5.87827 5.77551 3.84344 7.91878 2.34315C10.0621 0.842855 12.969 0 16 0C19.031 0 21.9379 0.842855 24.0812 2.34315C26.2245 3.84344 27.4286 5.87827 27.4286 8V16ZM16 27.2C17.2124 27.2 18.3752 26.8629 19.2325 26.2627C20.0898 25.6626 20.5714 24.8487 20.5714 24C20.5714 23.1513 20.0898 22.3374 19.2325 21.7373C18.3752 21.1371 17.2124 20.8 16 20.8C14.7876 20.8 13.6248 21.1371 12.7675 21.7373C11.9102 22.3374 11.4286 23.1513 11.4286 24C11.4286 24.8487 11.9102 25.6626 12.7675 26.2627C13.6248 26.8629 14.7876 27.2 16 27.2ZM22.8571 16V8C22.8571 6.72696 22.1347 5.50606 20.8487 4.60589C19.5628 3.70571 17.8186 3.2 16 3.2C14.1814 3.2 12.4372 3.70571 11.1513 4.60589C9.8653 5.50606 9.14286 6.72696 9.14286 8V16H22.8571Z"
          fill={color}
        />
      </svg>
    ),
    profile: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M53.5 28.4167C53.5 43.3291 41.8134 55.3333 27.5 55.3333C13.1866 55.3333 1.5 43.3291 1.5 28.4167C1.5 13.5043 13.1866 1.5 27.5 1.5C41.8134 1.5 53.5 13.5043 53.5 28.4167Z"
          fill="white"
          stroke={color}
          stroke-width="3"
        />
        <path
          d="M20.7435 19.8835C20.7435 23.7327 23.7749 26.8652 27.5 26.8652C31.2251 26.8652 34.2565 23.7327 34.2565 19.8835C34.2565 16.0343 31.2251 12.9018 27.5 12.9018C23.7749 12.9018 20.7435 16.0343 20.7435 19.8835ZM39.5115 42.38H41.0129V40.8285C41.0129 34.8414 36.2969 29.9681 30.5029 29.9681H24.4971C18.7016 29.9681 13.9871 34.8414 13.9871 40.8285V42.38H39.5115Z"
          fill={color}
        />
        <rect x="38" y="40" width="4" height="3" fill="white" />
        <rect
          x="29.8809"
          y="42.4641"
          width="6.03291"
          height="3.23579"
          transform="rotate(-49.2 29.8809 42.4641)"
          fill="white"
        />
        <path
          d="M50.3141 27.5525L46.4191 24.7964L33.9643 39.2582L33.1094 43.8902L37.8592 42.0143L50.3141 27.5525ZM43.9282 27.6887L47.8231 30.4448L43.9282 27.6887ZM33.9643 39.2582L37.8592 42.0143L33.9643 39.2582Z"
          fill={color}
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
    comment: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M16 0C7.2 0 0 6.36444 0 14.2222C0.0401735 16.1115 0.455403 17.9675 1.21602 19.6578C1.97663 21.348 3.06385 22.8307 4.4 24C4.4 25.0667 3.728 27.8578 0 32C3.792 31.8044 7.424 30.2222 10.352 27.5556C12.176 28.1422 14.096 28.4444 16 28.4444C24.8 28.4444 32 22.08 32 14.2222C32 6.36444 24.8 0 16 0ZM16 24.8889C8.928 24.8889 3.2 20.1067 3.2 14.2222C3.2 8.33778 8.928 3.55556 16 3.55556C23.072 3.55556 28.8 8.33778 28.8 14.2222C28.8 20.1067 23.072 24.8889 16 24.8889ZM14.4 17.7778V21.3333H17.6V17.7778H14.4ZM14.4 14.2222H17.6V7.11111H14.4V14.2222Z"
          fill={color}
        />
      </svg>
    ),
    notification: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M17.6 17.6H14.4V8H17.6V17.6ZM17.6 24H14.4V20.8H17.6V24ZM16 0C13.8989 0 11.8183 0.413852 9.87706 1.21793C7.93586 2.022 6.17203 3.20055 4.68629 4.68629C1.68571 7.68687 0 11.7565 0 16C0 20.2435 1.68571 24.3131 4.68629 27.3137C6.17203 28.7994 7.93586 29.978 9.87706 30.7821C11.8183 31.5861 13.8989 32 16 32C20.2435 32 24.3131 30.3143 27.3137 27.3137C30.3143 24.3131 32 20.2435 32 16C32 13.8989 31.5861 11.8183 30.7821 9.87706C29.978 7.93586 28.7994 6.17203 27.3137 4.68629C25.828 3.20055 24.0641 2.022 22.1229 1.21793C20.1817 0.413852 18.1012 0 16 0Z"
          fill={color}
        />
      </svg>
    ),
    close: (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        <path
          d="M15.3576 14.2156C15.4984 14.3578 15.5774 14.5498 15.5774 14.75C15.5774 14.9501 15.4984 15.1422 15.3576 15.2843C15.2142 15.423 15.0226 15.5004 14.8232 15.5004C14.6238 15.5004 14.4322 15.423 14.2888 15.2843L8.07319 9.05935L1.85756 15.2843C1.71421 15.423 1.5226 15.5004 1.32319 15.5004C1.12378 15.5004 0.932169 15.423 0.788814 15.2843C0.647977 15.1422 0.56897 14.9501 0.56897 14.75C0.56897 14.5498 0.647977 14.3578 0.788814 14.2156L7.01381 7.99997L0.788814 1.78435C0.669216 1.63862 0.608097 1.45363 0.617345 1.26534C0.626593 1.07705 0.705545 0.89893 0.838846 0.765629C0.972147 0.632328 1.15027 0.553375 1.33855 0.544128C1.52684 0.53488 1.71184 0.595999 1.85756 0.715596L8.07319 6.9406L14.2888 0.715596C14.4345 0.595999 14.6195 0.53488 14.8078 0.544128C14.9961 0.553375 15.1742 0.632328 15.3075 0.765629C15.4408 0.89893 15.5198 1.07705 15.529 1.26534C15.5383 1.45363 15.4772 1.63862 15.3576 1.78435L9.13256 7.99997L15.3576 14.2156Z"
          fill={color}
        />
      </svg>
    ),
  };

  if (svgList[name]) {
    return svgList[name];
  } else {
    return <h1>INVALID NAME</h1>;
  }
};

Icon.defaultProps = {
  size: 32,
  color: "#37519d",
};

export default Icon;
